import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Metamodel } from 'app/model/base/model';
import { commonCopySourceAdapts, CopySourceAdapter } from './copysource.adapter';
import { Model } from 'app/model/backendmodel';
import { ValueAdapter } from '../../value.adapter';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { partitionOptionConst } from 'app/registries/consts/metadata-consts';
import { Duration } from 'luxon';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';
import { SqlAnalyticsEndpointCopySource } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SqlAnalyticsEndpointCopySource),
    asynchronous: true,
    adapts: {
        ...commonCopySourceAdapts,
        query: '*',
        queryTimeout: '*',
        partitionOption: '*',
        partitionSettings: {
            partitionColumnName: '*',
            partitionUpperBound: '*',
            partitionLowerBound: '*'
        }
    }
})
export class SqlAnalyticsEndpointCopySourceAdapter extends CopySourceAdapter<SqlAnalyticsEndpointCopySource, Model.SqlAnalyticsEndpointCopySource> {
    protected override async _toResource(copySource: SqlAnalyticsEndpointCopySource): Promise<Model.SqlAnalyticsEndpointCopySource> {
        const result: Model.CopySource = this.getCommonCopySourceProperties(copySource, Metamodel.modelFor(SqlAnalyticsEndpointCopySource).name);
        result.query = ValueAdapter.toResource(copySource, SqlAnalyticsEndpointCopySource.QUERY);
        result.partitionOption = ValueAdapter.toResource(copySource, SqlAnalyticsEndpointCopySource.PARTITION_OPTION);
        if (SqlAnalyticsEndpointCopySource.PARTITION_OPTION.templateValue(copySource) != null) {
            result.partitionSettings = {
                partitionColumnName: ValueAdapter.toResource(
                    copySource,
                    SqlAnalyticsEndpointCopySource.PARTITION_COLUMN_NAME
                ),
                partitionUpperBound: ValueAdapter.toResource(
                    copySource,
                    SqlAnalyticsEndpointCopySource.PARTITION_UPPER_BOUND
                ),
                partitionLowerBound: ValueAdapter.toResource(
                    copySource,
                    SqlAnalyticsEndpointCopySource.PARTITION_LOWER_BOUND
                ),
                partitionNames: ValueAdapter.toResource(copySource, SqlAnalyticsEndpointCopySource.PARTITION_NAMES)
            };
        } else if (
            !stringIsNullOrEmpty(copySource.partitionOption) &&
            copySource.partitionOption === partitionOptionConst.dyamicRange
        ) {
            result.partitionSettings = {
                partitionColumnName: ValueAdapter.toResource(
                    copySource,
                    SqlAnalyticsEndpointCopySource.PARTITION_COLUMN_NAME
                ),
                partitionUpperBound: ValueAdapter.toResource(
                    copySource,
                    SqlAnalyticsEndpointCopySource.PARTITION_UPPER_BOUND
                ),
                partitionLowerBound: ValueAdapter.toResource(
                    copySource,
                    SqlAnalyticsEndpointCopySource.PARTITION_LOWER_BOUND
                )
            };
        }

        try {
            result.queryTimeout =
                typeof copySource.queryTimeout === 'number'
                    ? Duration.fromObject({ minutes: copySource.queryTimeout }).toFormat('hh:mm:ss')
                    : ValueAdapter.toResource(copySource, SqlAnalyticsEndpointCopySource.QUERY_TIMEOUT);
        } catch {
            this._logError('Failed to convert SqlAnalyticsEndpointCopySource queryTimeout into TimeSpan');
        }

        return result;
    }

    protected override async _toModel(resource: Model.SqlAnalyticsEndpointCopySource, copyActivity: CopyActivity): Promise<SqlAnalyticsEndpointCopySource> {
        const result = this.createCopySourceWithCommonProperties(
            SqlAnalyticsEndpointCopySource.MODEL,
            resource,
            copyActivity
        ) as SqlAnalyticsEndpointCopySource;
        ValueAdapter.toModel(result, SqlAnalyticsEndpointCopySource.QUERY, resource.query);

        if (resource.partitionOption !== undefined) {
            ValueAdapter.toModel(result, SqlAnalyticsEndpointCopySource.PARTITION_OPTION, resource.partitionOption);
        } else {
            delete result.partitionOption;
        }
        if (resource.partitionSettings != null) {
            ValueAdapter.toModel(
                result,
                SqlAnalyticsEndpointCopySource.PARTITION_NAMES,
                resource.partitionSettings.partitionNames
            );
            ValueAdapter.toModel(
                result,
                SqlAnalyticsEndpointCopySource.PARTITION_COLUMN_NAME,
                resource.partitionSettings.partitionColumnName
            );
            ValueAdapter.toModel(
                result,
                SqlAnalyticsEndpointCopySource.PARTITION_LOWER_BOUND,
                resource.partitionSettings.partitionLowerBound
            );
            ValueAdapter.toModel(
                result,
                SqlAnalyticsEndpointCopySource.PARTITION_UPPER_BOUND,
                resource.partitionSettings.partitionUpperBound
            );
        }

        try {
            if (resource.queryTimeout == null) {
                result.queryTimeout = undefined;
            } else {
                if (typeof resource.queryTimeout === 'string' && !isLogicAppExpression(resource.queryTimeout)) {
                    const parts = resource.queryTimeout.split(':');
                    if (parts.length === 3) {
                        const duration = Duration.fromObject({
                            hours: parseInt(parts[0], 10),
                            minutes: parseInt(parts[1], 10),
                            seconds: parseInt(parts[2], 10)
                        });
                        result.queryTimeout = duration.as('minutes');
                    } else {
                        this._logError('Failed to convert SqlAnalyticsEndpointCopySource queryTimeout into number');
                    }
                } else {
                    ValueAdapter.toModel(result, SqlAnalyticsEndpointCopySource.QUERY_TIMEOUT, resource.queryTimeout);
                }
            }
        } catch {
            this._logError('Failed to convert SqlAnalyticsEndpointCopySource queryTimeout into number');
        }
        return result;
    }
}
